import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    PhoneIcon,
    EnvelopeIcon,
    MapPinIcon,
    BookOpenIcon,
    UserGroupIcon
} from '@heroicons/react/24/outline';

const Footer = () => {
    const quickLinks = [
        { path: '/', label: 'Home' },
        { path: '/about', label: 'About Us' },
        { path: '/programmes', label: 'Programmes' },
        // { path: '/term-calendar', label: 'Term Calendar' },
        { path: '/gallery', label: 'Gallery' },
        { path: '/contact', label: 'Contact' }
    ];

    const programmes = [
        { path: '/programmes#early-years', label: 'Early Years' },
        { path: '/programmes#primary', label: 'Primary Years' },
        { path: '/programmes#clubs', label: 'School Activities' },
        { path: '/programmes#quran', label: "Qur'an Learning" }
    ];

    const contactInfo = [
        {
            icon: PhoneIcon,
            label: 'Phone',
            value: '+92 326 1116777',
            href: 'tel:+923261116777'
        },
        {
            icon: EnvelopeIcon,
            label: 'Email',
            value: 'admissions@aureusschool.com',
            href: 'mailto:admissions@aureusschool.com'
        },
        {
            icon: MapPinIcon,
            label: 'Address',
            value: 'F-78, Block \'B\', North Nazimabad, Karachi',
            href: null
        }
    ];

    return (
        <footer className="text-white bg-slate-900">
            {/* Islamic Pattern Background */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23fff' fill-opacity='0.1'%3E%3Cpath d='M30 30l15-15v30l-15-15zm0 0l-15 15h30l-15-15z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    backgroundSize: '60px 60px'
                }}></div>
            </div>

            <div className="relative">
                {/* Main Footer Content */}
                <div className="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">

                        {/* School Info */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                            className="lg:col-span-1"
                        >
                            <div className="flex items-center gap-3 mb-6">
                                <img
                                    src={require('../../assets/Logo - ACS-white.png')}
                                    alt="Aureus Cambridge School"
                                    className="w-auto h-12"
                                />
                                <div>
                                    <h3 className="text-2xl font-bold tracking-wider" style={{ fontFamily: 'Playfair Display, serif' }}>AUREUS</h3>
                                    <p className="text-xs font-semibold tracking-widest text-yellow-400" style={{ fontFamily: 'Playfair Display, serif' }}>CAMBRIDGE SCHOOL</p>
                                </div>
                            </div>

                            <p className="mb-6 leading-relaxed text-slate-300">
                                Where Knowledge Meets Faith. A modern Cambridge curriculum guided by Islamic values,
                                nurturing confident learners for success in both worlds.
                            </p>

                            <div className="flex items-center gap-2 text-yellow-400">
                                <BookOpenIcon className="w-5 h-5" />
                                <span className="text-sm font-medium">Cambridge Curriculum + Islamic Tarbiyah</span>
                            </div>
                        </motion.div>

                        {/* Quick Links */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            viewport={{ once: true }}
                        >
                            <h4 className="mb-6 text-lg font-bold text-yellow-400">Quick Links</h4>
                            <ul className="space-y-3">
                                {quickLinks.map((link, index) => (
                                    <li key={index}>
                                        <Link
                                            to={link.path}
                                            className="flex items-center no-underline transition-colors duration-200 text-slate-300 hover:text-yellow-400 group"
                                        >
                                            <span className="w-2 h-2 mr-3 transition-opacity bg-yellow-400 rounded-full opacity-0 group-hover:opacity-100"></span>
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* Programmes */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            viewport={{ once: true }}
                        >
                            <h4 className="mb-6 text-lg font-bold text-yellow-400">Our Programmes</h4>
                            <ul className="space-y-3">
                                {programmes.map((programme, index) => (
                                    <li key={index}>
                                        <Link
                                            to={programme.path}
                                            className="flex items-center no-underline transition-colors duration-200 text-slate-300 hover:text-yellow-400 group"
                                        >
                                            <span className="w-2 h-2 mr-3 transition-opacity bg-yellow-400 rounded-full opacity-0 group-hover:opacity-100"></span>
                                            {programme.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        {/* Contact Info */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            viewport={{ once: true }}
                        >
                            <h4 className="mb-6 text-lg font-bold text-yellow-400">Contact Us</h4>
                            <ul className="space-y-4">
                                {contactInfo.map((contact, index) => (
                                    <li key={index} className="flex items-start">
                                        <contact.icon className="flex-shrink-0 w-5 h-5 mt-1 mr-3 text-yellow-400" />
                                        <div>
                                            <p className="mb-1 text-sm font-medium text-slate-300">{contact.label}</p>
                                            {contact.href ? (
                                                <a
                                                    href={contact.href}
                                                    className="text-white no-underline transition-colors duration-200 hover:text-yellow-400"
                                                >
                                                    {contact.value}
                                                </a>
                                            ) : (
                                                <p className="text-white">{contact.value}</p>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>

                            {/* Apply Now CTA */}
                            <div className="mt-8">
                                <Link
                                    to="/apply-online"
                                    className="flex items-center px-6 py-3 font-semibold no-underline transition-all duration-300 bg-yellow-400 rounded-lg hover:bg-yellow-500 text-slate-900 group"
                                >
                                    <UserGroupIcon className="w-5 h-5 mr-2" />
                                    Apply Now
                                </Link>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="border-t border-slate-800">
                    <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
                        <div className="flex flex-col items-center justify-between md:flex-row">
                            <motion.p
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                transition={{ duration: 0.6 }}
                                viewport={{ once: true }}
                                className="mb-4 text-sm text-slate-400 md:mb-0"
                            >
                                © 2025 Aureus Cambridge School. All rights reserved.
                            </motion.p>

                            <motion.p
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                transition={{ duration: 0.6, delay: 0.1 }}
                                viewport={{ once: true }}
                                className="text-sm text-slate-400"
                            >
                                Designed and Developed by{' '}
                                <a
                                    href="https://marfinn.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-medium text-yellow-400 no-underline transition-colors duration-200 hover:text-yellow-300"
                                >
                                    MarFinn
                                </a>
                            </motion.p>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
